// POLICY QUESTIONNAIRE: MANDATORY DOCUMENTS BY ENTITY TYPE (v1_814, field 4).
//
// The questionnaire asks for the house policy floor by five entity types the
// MLRO recognises. The floor (firmMandatedDocs, Guard 0 in the proportionality
// review) matches on the board's controlled entity type keys (firm-policy.js
// ENTITY_TYPES). This module is the fixed bridge between the two, and it holds
// the CompliantID starter lists the questionnaire proposes.
//
// STARTER LISTS ARE PROPOSALS, NOT POLICY. Until the firm signs a questionnaire
// the default stays general AML principles (no floor), and the page shows the
// starter list as "CompliantID default, pending your confirmation". A starter
// list reaches the floor only through a signed questionnaire. STARTER_LISTS_STATUS
// records the platform owner's approval of the wording itself.
//
// A floor only ever protects items (the review withdraws less); it never adds a
// request. So a list is kept to what a regulated firm would ask of every client
// of the type, with conditional wording where a document applies only sometimes.
// No hyphens or dashes: the MLRO and the client read these.

// Approved by the platform owner 25/09/26, with two additions (partnership:
// the register or schedule of partners; trust: the schedule of parties).
export const STARTER_LISTS_STATUS = 'approved_2026_09_25';

export const QUESTIONNAIRE_ENTITY_TYPES = Object.freeze([
  'private_company', 'regulated_firm', 'partnership', 'trust', 'individual',
]);

export const QUESTIONNAIRE_ENTITY_LABELS = Object.freeze({
  private_company: 'Private company (including holding companies and SPVs)',
  regulated_firm: 'Regulated firm',
  partnership: 'Partnership',
  trust: 'Trust',
  individual: 'Individual',
});

// Which board entity types each questionnaire type's list applies to
// (approved 25/09/26, decisions A and B).
//  - private company and regulated firm: the approved mapping, with
//    FAMILY_OFFICE under private company and GAMING under regulated firm.
//  - partnership, trust, individual: recorded, shown and marked "Not yet
//    enforced" until each has its own board type key. Never mapped to ALL: the
//    floor's match is fuzzy, and a probe showed an ALL list protecting company
//    items it was never written for (an individual's address proof protected a
//    company bank statement; a partnership agreement protected a certificate of
//    incorporation).
export const QUESTIONNAIRE_TO_BOARD_TYPES = Object.freeze({
  // v1_814b (approved 25/09/26): the full entity type vocabulary the case board
  // uses (src/shared/sector-risk.js ENTITY_TYPES), each placed under one type.
  private_company: Object.freeze([
    'CORPORATE_TREASURY', 'OTHER', 'FAMILY_OFFICE', 'TRADING_COMPANY', 'LISTED_COMPANY', 'PUBLIC_AUTHORITY',
    'MANUFACTURING', 'TECHNOLOGY', 'HEALTHCARE_EDUCATION', 'TRANSPORT_LOGISTICS', 'LEGAL_ACCOUNTING', 'REAL_ESTATE',
    'CONSTRUCTION', 'IMPORT_EXPORT', 'HOSPITALITY_RETAIL', 'ECOMMERCE', 'PHARMACEUTICALS', 'RECRUITMENT',
    'MOTOR_TRADE', 'TRAVEL', 'HIGH_VALUE_DEALER', 'CASH_INTENSIVE', 'ARMS_DEFENCE', 'EXTRACTIVES',
    'COMMODITY_TRADING', 'ADULT_ENTERTAINMENT', 'UNREGULATED_SCHEME', 'NPO_HIGH_RISK', 'SHELL_HOLDING',
  ]),
  regulated_firm: Object.freeze([
    'SUPERVISED_FI', 'SPOT_BROKER', 'CFD_BROKER', 'PROP_FIRM', 'PROP_TRADING', 'ASSET_MANAGER',
    'REGULATED_FUND', 'PENSION_SCHEME', 'MSB', 'VASP', 'GAMING', 'TCSP',
  ]),
  partnership: Object.freeze([]),
  trust: Object.freeze([]),
  individual: Object.freeze([]),
});

// A board entity type outside the vocabulary (a model invented value such as
// OPERATING_COMPANY, or prose like "OPERATING_COMPANY — UK FCA-authorised
// investment advisory firm") still reaches a list, never none: a regulatory
// word places it under regulated firm, anything else corporate under private
// company. Returns the board key whose list applies, or null for an empty type.
const REGULATED_WORDS = /\b(AUTHORI[SZ]ED|REGULATED|LICENSED|LICENCED|SUPERVISED|REGISTERED_WITH|FCA|FINMA|BAFIN|SEC|CYSEC|MAS|VQF|FSC)\b/;
export function floorBoardTypeFor(entityType) {
  const raw = String(entityType || '').trim();
  if (!raw) return null;
  const up = raw.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
  for (const qt of QUESTIONNAIRE_ENTITY_TYPES) if (QUESTIONNAIRE_TO_BOARD_TYPES[qt].includes(up)) return up;
  if (REGULATED_WORDS.test(up.replace(/_/g, ' '))) return 'SUPERVISED_FI';
  return 'OTHER';
}
export function questionnaireTypeForBoardType(entityType) {
  const b = floorBoardTypeFor(entityType);
  return b ? QUESTIONNAIRE_ENTITY_TYPES.find(qt => QUESTIONNAIRE_TO_BOARD_TYPES[qt].includes(b)) || null : null;
}

// The admin document checklist offers two keys no board type ever carries, so
// documents listed under them protected nothing (v1_812 report, section 3.3).
// They resolve to the board types they describe.
export const CHECKLIST_KEY_TO_BOARD_TYPES = Object.freeze({
  BROKER: Object.freeze(['SPOT_BROKER', 'CFD_BROKER', 'PROP_FIRM']),
  HOLDING_SPV: Object.freeze(['CORPORATE_TREASURY', 'OTHER']),
});

export const STARTER_DOCS = Object.freeze({
  private_company: Object.freeze([
    'Certificate of incorporation',
    'Register of directors, current and complete',
    'Register of shareholders or members, current and complete',
    'Structure chart tracing ownership to the ultimate individual owners, where the company is owned through other companies or trusts',
    'Most recent financial statements, or management accounts where the company has not yet filed accounts',
  ]),
  regulated_firm: Object.freeze([
    'Certificate of incorporation',
    'Register of directors, current and complete',
    'Register of shareholders or members, current and complete',
    'Structure chart tracing ownership to the ultimate individual owners, where the firm is owned through other companies or trusts',
    'Evidence of the licence, authorisation or registration covering the services the firm provides (a copy, or the public register entry)',
    'AML and CTF policy (anti money laundering and counter terrorist financing)',
  ]),
  partnership: Object.freeze([
    'Partnership agreement',
    'Certificate of registration, for a limited or limited liability partnership',
    "Register or schedule of partners with each partner's share of capital and profits",
  ]),
  trust: Object.freeze([
    'Trust deed',
    'Any deed of variation, or deed appointing or removing trustees',
    'Schedule of the settlor, trustees, protector if any, and beneficiaries',
  ]),
  individual: Object.freeze([
    'Proof of residential address',
  ]),
});

// v1_818: every board type a specific list may be given for (the vocabulary the
// broad lists apply to), in the questionnaire's order.
export const SPECIFIC_DOC_TYPES = Object.freeze([...QUESTIONNAIRE_TO_BOARD_TYPES.regulated_firm, ...QUESTIONNAIRE_TO_BOARD_TYPES.private_company]);
export const boardTypeLabel = (bt) => String(bt || '').toLowerCase().replace(/_/g, ' ');

// v1_818: THE ADMIN CHECKLIST'S OWN LIST FOR ONE BOARD TYPE. The operator's
// documentChecklist.byType for the type, and the two group keys (BROKER,
// HOLDING_SPV) that cover it. A type with such a list keeps it: the
// questionnaire's broad list does not apply on top.
export function adminSpecificDocs(documentChecklist, boardType) {
  const dc = documentChecklist && typeof documentChecklist === 'object' ? documentChecklist : {};
  const by = dc.byType && typeof dc.byType === 'object' ? dc.byType : {};
  const clean = (v) => (Array.isArray(v) ? v : []).map(x => String(x || '').trim()).filter(Boolean);
  const out = boardType ? clean(by[boardType]) : [];
  for (const [ck, types] of Object.entries(CHECKLIST_KEY_TO_BOARD_TYPES)) if (boardType && types.includes(boardType)) out.push(...clean(by[ck]));
  return [...new Set(out)];
}
// Every board type the admin checklist gives its own list: { TYPE: [docs] }.
export function adminSpecificByType(documentChecklist) {
  const out = {};
  for (const bt of SPECIFIC_DOC_TYPES) { const d = adminSpecificDocs(documentChecklist, bt); if (d.length) out[bt] = d; }
  return out;
}

// Build the floor's requiredDocsByEntityType (board keys) from signed answers:
// { private_company: [...], regulated_firm: [...], ..., specific: { VASP: [...] } }.
// Unkeyed types map to nothing until they have a board key. Duplicates collapse.
// v1_818: a type given its own list (specific) takes that list in place of its
// questionnaire type's broad list, never both. Pure.
export function floorFromQuestionnaireDocs(byQuestionnaireType) {
  const out = {};
  const src = byQuestionnaireType && typeof byQuestionnaireType === 'object' ? byQuestionnaireType : {};
  const clean = (v) => [...new Set((Array.isArray(v) ? v : []).map(x => String(x || '').trim()).filter(Boolean))];
  const specific = specificDocsOf(src);
  for (const qt of QUESTIONNAIRE_ENTITY_TYPES) {
    const docs = clean(src[qt]);
    if (!docs.length) continue;
    for (const bt of QUESTIONNAIRE_TO_BOARD_TYPES[qt]) if (!specific[bt]) out[bt] = [...docs];
  }
  for (const [bt, docs] of Object.entries(specific)) out[bt] = docs;
  return out;
}
// The specific lists of an answer, cleaned: only board types, only non empty.
export function specificDocsOf(mandatoryDocsValue) {
  const sp = mandatoryDocsValue && mandatoryDocsValue.specific && typeof mandatoryDocsValue.specific === 'object' ? mandatoryDocsValue.specific : {};
  const out = {};
  for (const [k, v] of Object.entries(sp)) {
    const bt = String(k || '').trim().toUpperCase();
    if (!SPECIFIC_DOC_TYPES.includes(bt)) continue;
    const docs = [...new Set((Array.isArray(v) ? v : []).map(x => String(x || '').trim()).filter(Boolean))];
    if (docs.length) out[bt] = docs;
  }
  return out;
}

// Questionnaire types whose list is recorded but reaches no board type.
// v1_832: partnership, trust and individual are reached by the client's legal
// form (src/shared/legal-form.js), not by an entity type, so no type is left
// unreachable.
export function unenforcedQuestionnaireTypes() {
  return [];
}
